/**
 * Dashboard — Scanări și interacțiuni (card sumar + popup detaliat).
 */
import {
  getClientInterfaceAnalyticsBackend,
  resetClientInterfaceAnalyticsBackend
} from '../services/backend.js?v=83.79';
import { getLastClaimsRows, getLastCommunityLeadsRows } from '../services/claims.js?v=83.79';
import { logger } from '../utils/logger.js?v=83.79';
import { parseFirestoreTimestamp } from '../utils/helpers.js?v=83.79';
import { showSnackbar } from '../utils/ui.js?v=83.79';
import { getClientAnalyticsMetricIconHtml } from './client-analytics-icons.js?v=83.79';
import { getCurrentSettings } from '../services/settings.js?v=83.79';
import { hasFeature, featureLockedMessage } from '../config/plans.js?v=83.79';

const PERIOD_LABELS = {
  today: 'Azi',
  '7d': '7 zile',
  '30d': '30 zile',
  month: 'Luna asta',
  custom: 'Perioadă aleasă',
  total: 'Total'
};
void PERIOD_LABELS;

/** Module afișate în grid (fără scanări QR). */
const MODULE_GRID_KEYS = [
  'menu',
  'wifi',
  'review',
  'social_tiktok',
  'reservation',
  'location',
  'hours',
  'social_instagram',
  'social_facebook',
  'community',
  'images'
];

const MODULE_CHIP_COLORS = {
  menu: 'green',
  wifi: 'purple',
  review: 'amber',
  social_tiktok: 'dark',
  reservation: 'orange',
  location: 'blue',
  hours: 'red',
  social_instagram: 'pink',
  social_facebook: 'facebook',
  community: 'teal',
  images: 'violet'
};

let activePeriod = '7d';
let lastPayload = null; // payload-ul tab-ului „Rezumat” (și al cardului mic)
let activeTab = 'summary';
let customFrom = '';
let customTo = '';
let activeMonth = '';
let monthsPayload = null;
let hoursPayload = null;
let loadSeq = 0;
let isLoading = false;
let isResetting = false;
let dashboardBound = false;

function getPopup() {
  return document.getElementById('clientInterfaceAnalyticsPopup');
}

function isPopupOpen() {
  return getPopup()?.classList.contains('active') === true;
}

function parseRowDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = parseFirestoreTimestamp(value);
  if (parsed) return parsed;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function localKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Rândul e în intervalul payload-ului ({from,to} chei de zi); fără interval = Total. */
function isInPeriod(dateValue, range) {
  const date = parseRowDate(dateValue);
  if (!range) return true;
  if (!date) return false;
  const k = localKey(date);
  return k >= range.from && k <= range.to;
}

function countMarketingAgreements(range) {
  const claims = getLastClaimsRows().filter(
    (row) => row?.marketingAccepted === true && isInPeriod(row.date || row.createdAt, range)
  );
  const community = getLastCommunityLeadsRows().filter((row) =>
    isInPeriod(row.createdAt || row.date, range)
  );
  return claims.length + community.length;
}

function getMetricMap(payload) {
  const metrics = Array.isArray(payload?.metrics) ? payload.metrics : [];
  const map = {};
  metrics.forEach((m) => {
    map[m.key] = m;
  });
  return map;
}

function getModuleMetrics(payload) {
  const map = getMetricMap(payload);
  return MODULE_GRID_KEYS.map((key) => ({
    key,
    label: map[key]?.label || key,
    value: Number(map[key]?.value) || 0
  }));
}

function getAllModuleMetrics(payload) {
  const metrics = Array.isArray(payload?.metrics) ? payload.metrics : [];
  return metrics.filter((m) => m.key !== 'interface');
}

function getTopModule(payload) {
  const modules = getAllModuleMetrics(payload);
  let top = null;
  modules.forEach((m) => {
    const value = Number(m.value) || 0;
    if (value <= 0) return;
    if (!top || value > top.value) {
      top = { key: m.key, label: m.label || m.key, value };
    }
  });
  return top;
}

function getQrScans(payload) {
  const map = getMetricMap(payload);
  if (map.interface) return Number(map.interface.value) || 0;
  return Number(payload?.interfaceViews) || 0;
}

function formatPercent(value, total) {
  if (!total || total <= 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
}

function formatAccessLabel(count) {
  const n = Number(count) || 0;
  return n === 1 ? '1 accesare' : `${n} accesări`;
}

function renderPeriodButtons() {
  const wrap = document.getElementById('clientInterfaceAnalyticsPeriods');
  if (!wrap) return;
  wrap.querySelectorAll('[data-analytics-period]').forEach((btn) => {
    const p = btn.getAttribute('data-analytics-period');
    const isActive = p === activePeriod;
    btn.classList.toggle('client-analytics-period-btn--active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

function renderDashboardSummary(payload) {
  const linesEl = document.getElementById('clientInterfaceAnalyticsSummaryLines');
  if (!linesEl) return;

  if (!payload) {
    linesEl.innerHTML = '<span class="cia-summary-card__line">Se încarcă...</span>';
    return;
  }

  const qr = getQrScans(payload);
  const marketing = countMarketingAgreements(payload.range || null);
  const top = getTopModule(payload);

  linesEl.innerHTML = `
    <span class="cia-summary-card__line"><strong>Scanări QR:</strong> ${qr}</span>
    <span class="cia-summary-card__line"><strong>Acord marketing:</strong> ${marketing}</span>
    <span class="cia-summary-card__line"><strong>Top modul:</strong> ${top ? top.label : '—'}</span>
  `;
}

function renderHeroCards(payload) {
  const hero = document.getElementById('clientInterfaceAnalyticsHero');
  if (!hero) return;

  if (!payload) {
    hero.innerHTML = '<div class="dashboard-empty-state">Se încarcă...</div>';
    return;
  }

  const qr = getQrScans(payload);
  const marketing = countMarketingAgreements(payload.range || null);
  const top = getTopModule(payload);
  const periodHint = 'în perioada selectată';

  hero.innerHTML = `
    <article class="cia-hero-card cia-hero-card--qr">
      <span class="cia-hero-card__icon-wrap cia-hero-card__icon-wrap--green" aria-hidden="true">
        <svg class="client-analytics-icon client-analytics-icon--stroke" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="14" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="3" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
          <path d="M14 14h2v2h-2zM18 14h3v3h-3zM14 18h2v3h-2zM18 18h1v1h-1zM20 18h1v1h-1z" fill="currentColor" stroke="none"/>
        </svg>
      </span>
      <div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Scanări QR</span>
        <span class="cia-hero-card__value">${qr}</span>
        ${deltaHtml(qr, payload.prev ? payload.prev.interfaceViews : null, payload.prev ? rangeLabel(payload.prev) : '') || `<span class="cia-hero-card__hint">${periodHint}</span>`}
      </div>
    </article>
    <article class="cia-hero-card cia-hero-card--marketing">
      <span class="cia-hero-card__icon-wrap cia-hero-card__icon-wrap--blue" aria-hidden="true">
        <svg class="client-analytics-icon client-analytics-icon--stroke" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Acord marketing</span>
        <span class="cia-hero-card__value">${marketing}</span>
        <span class="cia-hero-card__hint">${periodHint}</span>
      </div>
    </article>
    <article class="cia-hero-card cia-hero-card--top">
      <span class="cia-hero-card__icon-wrap cia-hero-card__icon-wrap--purple" aria-hidden="true">
        <svg class="client-analytics-icon client-analytics-icon--stroke" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M4 22h16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Top modul accesat</span>
        <span class="cia-hero-card__value cia-hero-card__value--text">${top ? top.label : '—'}</span>
        <span class="cia-hero-card__hint">${top ? formatAccessLabel(top.value) : periodHint}</span>
      </div>
    </article>
  `;
}

function renderModuleGrid(payload) {
  const grid = document.getElementById('clientInterfaceAnalyticsGrid');
  if (!grid) return;

  const modules = getModuleMetrics(payload);
  const total = modules.reduce((sum, m) => sum + m.value, 0);

  if (!modules.length) {
    grid.innerHTML =
      '<div class="dashboard-empty-state">Nu există date pentru perioada selectată.</div>';
    return;
  }

  grid.innerHTML = modules
    .map((m) => {
      const color = MODULE_CHIP_COLORS[m.key] || 'slate';
      return `
    <article class="cia-module-chip cia-module-chip--${color}">
      <span class="cia-module-chip__icon" aria-hidden="true">${getClientAnalyticsMetricIconHtml(m.key)}</span>
      <span class="cia-module-chip__name">${m.label}</span>
      <span class="cia-module-chip__value">${m.value}</span>
      <span class="cia-module-chip__pct">${formatPercent(m.value, total)}</span>
    </article>`;
    })
    .join('');
}

/* ---------- formatare ---------- */
const RO_MONTHS = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const RO_MONTHS_LONG = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
const RO_DAYS = ['luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă', 'duminică'];
const RO_DAYS_SHORT = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'];
const HEAT_BUCKETS = [
  { label: '00–06', from: 0, to: 6 },
  { label: '06–09', from: 6, to: 9 },
  { label: '09–12', from: 9, to: 12 },
  { label: '12–15', from: 12, to: 15 },
  { label: '15–18', from: 15, to: 18 },
  { label: '18–21', from: 18, to: 21 },
  { label: '21–24', from: 21, to: 24 }
];

function esc(v) {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function dayLabel(key) {
  const [, m, d] = String(key || '').split('-').map(Number);
  return m ? `${d} ${RO_MONTHS[m - 1]}` : '';
}

function rangeLabel(range) {
  if (!range) return 'De la început';
  const y = range.to.slice(0, 4);
  if (range.from === range.to) return `${dayLabel(range.from)} ${y}`;
  const sameYear = range.from.slice(0, 4) === y;
  return `${dayLabel(range.from)}${sameYear ? '' : ` ${range.from.slice(0, 4)}`} – ${dayLabel(range.to)} ${y}`;
}

function monthLong(ym) {
  const [y, m] = String(ym || '').split('-').map(Number);
  return m ? `${RO_MONTHS_LONG[m - 1]} ${y}` : '—';
}

function shiftYm(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

function currentYm() {
  return new Date().toISOString().slice(0, 7);
}

/** Pastilă scurtă (▲ 23% / nou / la fel) + „față de …” pe rândul de dedesubt. */
function deltaHtml(cur, prev, vsText) {
  if (prev == null) return '';
  let pill;
  if (!prev) {
    pill = cur ? '<span class="cia-delta cia-delta--up">nou</span>' : '<span class="cia-delta cia-delta--flat">la fel</span>';
  } else {
    const pct = Math.round(((cur - prev) / prev) * 100);
    pill = pct === 0
      ? '<span class="cia-delta cia-delta--flat">la fel</span>'
      : `<span class="cia-delta cia-delta--${pct > 0 ? 'up' : 'down'}">${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%</span>`;
  }
  return pill + (vsText ? `<span class="cia-hero-card__hint">față de ${esc(vsText)}</span>` : '');
}

/* ---------- Rezumat: grafic pe zile ---------- */
function renderDaily(payload) {
  const box = document.getElementById('ciaDaily');
  const axis = document.getElementById('ciaDailyAxis');
  const peakEl = document.getElementById('ciaDailyPeak');
  const card = document.getElementById('ciaDailyCard');
  if (!box || !axis) return;
  const series = Array.isArray(payload?.series) ? payload.series : [];
  if (card) card.hidden = series.length < 2;
  if (series.length < 2) {
    box.innerHTML = '';
    axis.innerHTML = '';
    if (peakEl) peakEl.textContent = '';
    return;
  }
  const max = Math.max(1, ...series.map((d) => d.views));
  const peak = series.reduce((a, d) => (d.views > a.views ? d : a), series[0]);
  box.innerHTML = series
    .map((d) => {
      const h = Math.round((d.views / max) * 100);
      const isPeak = d.views > 0 && d === peak;
      return `<span class="cia-bar${isPeak ? ' is-peak' : ''}" style="height:${Math.max(h, d.views ? 4 : 2)}%" title="${esc(dayLabel(d.date))}: ${d.views} scanări"></span>`;
    })
    .join('');
  const idx = [0, Math.floor((series.length - 1) / 2), series.length - 1];
  axis.innerHTML = idx.map((i) => `<span>${esc(dayLabel(series[i].date))}</span>`).join('');
  if (peakEl) peakEl.textContent = peak.views ? `Vârf: ${dayLabel(peak.date)} · ${peak.views} scanări` : 'Încă nu sunt scanări';
}

/* ---------- Pe luni ---------- */
function renderMonths(payload) {
  const label = document.getElementById('ciaMonthLabel');
  const next = document.getElementById('ciaMonthNext');
  if (label) label.textContent = monthLong(activeMonth);
  if (next) next.disabled = activeMonth >= currentYm();
  const kpis = document.getElementById('ciaMonthKpis');
  const chart = document.getElementById('ciaMonthsChart');
  const axis = document.getElementById('ciaMonthsAxis');
  const table = document.getElementById('ciaMonthTable');
  if (!payload) {
    const loading = '<div class="dashboard-empty-state">Se încarcă...</div>';
    if (kpis) kpis.innerHTML = loading;
    if (chart) chart.innerHTML = '';
    if (table) table.innerHTML = '';
    return;
  }
  const qr = getQrScans(payload);
  const inter = Number(payload.interactions) || 0;
  const prev = payload.prev;
  const prevName = prev ? monthLong(prev.from.slice(0, 7)).split(' ')[0].toLowerCase() : '';
  const months = Array.isArray(payload.months) ? payload.months : [];
  const best = months.reduce((a, m) => (m.views > (a?.views || 0) ? m : a), null);
  const inProgress = activeMonth === currentYm();
  if (kpis) {
    kpis.innerHTML = `
      <article class="cia-hero-card"><div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Scanări QR</span>
        <span class="cia-hero-card__value">${qr}</span>
        ${prev ? deltaHtml(qr, prev.interfaceViews, prevName) : ''}
        ${inProgress ? '<span class="cia-hero-card__hint">luna e în curs</span>' : ''}
      </div></article>
      <article class="cia-hero-card"><div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Interacțiuni</span>
        <span class="cia-hero-card__value">${inter}</span>
        ${prev ? deltaHtml(inter, prev.interactions, prevName) : ''}
      </div></article>
      <article class="cia-hero-card"><div class="cia-hero-card__body">
        <span class="cia-hero-card__label">Cea mai bună lună</span>
        <span class="cia-hero-card__value cia-hero-card__value--text">${best ? esc(monthLong(best.month).split(' ')[0]) : '—'}</span>
        <span class="cia-hero-card__hint">${best ? `${best.views} scanări` : 'încă fără date'}</span>
      </div></article>`;
  }
  if (chart && axis) {
    const max = Math.max(1, ...months.map((m) => m.views));
    chart.innerHTML = months
      .map((m) => {
        const h = Math.round((m.views / max) * 100);
        const on = m.month === activeMonth;
        return `<button type="button" class="cia-mbar${on ? ' is-on' : ''}" data-cia-month="${esc(m.month)}" aria-label="${esc(monthLong(m.month))}: ${m.views} scanări">
          <span class="cia-mbar__v">${m.views || ''}</span><span class="cia-mbar__b" style="height:${Math.max(h, m.views ? 4 : 2)}%"></span></button>`;
      })
      .join('');
    axis.innerHTML = months.map((m) => `<span>${esc(RO_MONTHS[Number(m.month.slice(5)) - 1])}</span>`).join('');
  }
  if (table) {
    const map = getMetricMap(payload);
    const pt = prev?.totals || {};
    const rows = MODULE_GRID_KEYS.map((k) => ({ k, label: map[k]?.label || k, cur: Number(map[k]?.value) || 0, old: Number(pt[k]) || 0 }))
      .filter((r) => r.cur || r.old)
      .sort((a, b) => b.cur - a.cur);
    table.innerHTML = rows.length
      ? `<div class="cia-table__row cia-table__row--head"><span>Modul</span><span>${esc(prevName || 'Luna trecută')}</span><span>${esc(monthLong(activeMonth).split(' ')[0].toLowerCase())}</span><span>Trend</span></div>` +
        rows
          .map((r) => {
            let trend = '<span class="cia-delta cia-delta--flat">—</span>';
            if (r.old && r.cur !== r.old) {
              const pct = Math.round(((r.cur - r.old) / r.old) * 100);
              trend = `<span class="cia-delta cia-delta--${pct > 0 ? 'up' : 'down'}">${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%</span>`;
            } else if (!r.old && r.cur) {
              trend = '<span class="cia-delta cia-delta--up">nou</span>';
            }
            return `<div class="cia-table__row"><span>${esc(r.label)}</span><span>${r.old}</span><span><strong>${r.cur}</strong></span><span>${trend}</span></div>`;
          })
          .join('')
      : '<div class="dashboard-empty-state">Nicio interacțiune în luna aceasta.</div>';
  }
}

/* ---------- Când vin clienții ---------- */
function renderHours(payload) {
  const insight = document.getElementById('ciaHeatInsight');
  const heat = document.getElementById('ciaHeat');
  const top = document.getElementById('ciaTopBars');
  if (!payload || !payload.heat) {
    const loading = '<div class="dashboard-empty-state">Se încarcă...</div>';
    if (heat) heat.innerHTML = loading;
    if (insight) insight.innerHTML = '';
    if (top) top.innerHTML = '';
    return;
  }
  const g = payload.heat.grid || [];
  const cells = g.map((row) => HEAT_BUCKETS.map((b) => row.slice(b.from, b.to).reduce((a, v) => a + v, 0)));
  const max = Math.max(0, ...cells.flat());
  const level = (v) => (!v ? 0 : v >= max * 0.66 ? 3 : v >= max * 0.33 ? 2 : 1);
  if (heat) {
    heat.innerHTML =
      '<span></span>' + HEAT_BUCKETS.map((b) => `<span class="cia-heat__h">${b.label}</span>`).join('') +
      cells
        .map((row, d) => `<span class="cia-heat__d">${RO_DAYS_SHORT[d]}</span>` +
          row.map((v, i) => `<span class="cia-heat__c l${level(v)}" title="${RO_DAYS[d]}, ${HEAT_BUCKETS[i].label}: ${v} scanări"></span>`).join(''))
        .join('');
  }
  if (insight) {
    if (!payload.heat.total) {
      insight.innerHTML = '<strong>Încă nu sunt destule scanări în perioada aleasă.</strong><span>Revino după ce clienții încep să scaneze codul QR.</span>';
    } else {
      const byDay = cells.map((r) => r.reduce((a, v) => a + v, 0));
      const byBucket = HEAT_BUCKETS.map((_, i) => cells.reduce((a, r) => a + r[i], 0));
      const d1 = byDay.indexOf(Math.max(...byDay));
      const b1 = byBucket.indexOf(Math.max(...byBucket));
      const days = byDay.map((v, i) => ({ v, i })).sort((a, b) => b.v - a.v);
      const d2 = days[1] && days[1].v >= days[0].v * 0.8 ? days[1].i : -1;
      const dayTxt = d2 >= 0 ? `${RO_DAYS[Math.min(d1, d2)]} și ${RO_DAYS[Math.max(d1, d2)]}` : RO_DAYS[d1];
      const b = HEAT_BUCKETS[b1];
      insight.innerHTML = `<strong>Cele mai multe scanări: ${esc(dayTxt)}, între ${b.from}:00 și ${b.to === 24 ? '24' : b.to}:00</strong><span>Bun moment pentru o ofertă sau o campanie.</span>`;
    }
  }
  if (top) {
    const mods = getModuleMetrics(payload).filter((m) => m.value > 0).sort((a, b) => b.value - a.value).slice(0, 6);
    const mx = Math.max(1, ...mods.map((m) => m.value));
    top.innerHTML = mods.length
      ? mods.map((m) => `<div class="cia-top__row"><span class="cia-top__name">${esc(m.label)}</span><span class="cia-top__track"><span style="width:${Math.round((m.value / mx) * 100)}%"></span></span><span class="cia-top__v">${m.value}</span></div>`).join('')
      : '<div class="dashboard-empty-state">Nicio interacțiune în perioada aleasă.</div>';
  }
}

function renderRangeLabel() {
  const el = document.getElementById('ciaRangeLabel');
  if (!el) return;
  const p = activeTab === 'hours' ? hoursPayload : lastPayload;
  el.textContent = activeTab === 'months' ? '' : p ? rangeLabel(p.range) + (activeTab === 'hours' && !p.range ? ' · ultimele 90 de zile' : '') : '';
}

function renderTabs() {
  document.querySelectorAll('#ciaTabs [data-cia-tab]').forEach((b) => {
    const on = b.dataset.ciaTab === activeTab;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-selected', on ? 'true' : 'false');
  });
  document.querySelectorAll('#clientInterfaceAnalyticsPopup [data-cia-pane]').forEach((p) => {
    p.hidden = p.dataset.ciaPane !== activeTab;
  });
  const filters = document.getElementById('ciaFiltersRow');
  if (filters) filters.hidden = activeTab === 'months';
  const panel = document.getElementById('ciaRangePanel');
  if (panel && activeTab === 'months') panel.hidden = true;
}

function renderAllViews() {
  renderPeriodButtons();
  renderDashboardSummary(lastPayload);
  if (isPopupOpen()) {
    renderTabs();
    renderRangeLabel();
    if (activeTab === 'summary') {
      renderHeroCards(lastPayload);
      renderDaily(lastPayload);
      renderModuleGrid(lastPayload);
    } else if (activeTab === 'months') {
      renderMonths(monthsPayload);
    } else {
      renderHours(hoursPayload);
    }
  }
}

function setLoadingState(loading) {
  const root = document.getElementById('clientInterfaceAnalyticsRoot');
  if (root) root.classList.toggle('client-analytics--loading', loading);
  const popup = getPopup();
  if (popup) popup.classList.toggle('cia-is-loading', loading);
}

function periodKey() {
  return activePeriod === 'custom' ? `custom:${customFrom}:${customTo}` : activePeriod;
}

function periodParams() {
  const p = { };
  if (activePeriod === 'custom') {
    p.from = customFrom;
    p.to = customTo;
  }
  return p;
}

async function loadTab(tab) {
  const seq = ++loadSeq;
  isLoading = true;
  setLoadingState(true);
  try {
    if (tab === 'months') {
      if (!activeMonth) activeMonth = currentYm();
      monthsPayload = null;
      renderAllViews();
      const data = await getClientInterfaceAnalyticsBackend('month', { month: activeMonth, withMonths: true });
      if (seq !== loadSeq) return;
      monthsPayload = data;
    } else if (tab === 'hours') {
      hoursPayload = null;
      renderAllViews();
      const data = await getClientInterfaceAnalyticsBackend(activePeriod, { ...periodParams(), withHeat: true });
      if (seq !== loadSeq) return;
      hoursPayload = { ...data, _key: periodKey() };
    } else {
      const data = await getClientInterfaceAnalyticsBackend(activePeriod, periodParams());
      if (seq !== loadSeq) return;
      lastPayload = { ...data, _key: periodKey() };
    }
    renderAllViews();
  } catch (err) {
    if (seq !== loadSeq) return;
    logger.warn('[client-interface-analytics] load failed:', err);
    const msg = err?.message && /perioad|dat|lun/i.test(err.message) ? err.message : 'Nu s-au putut încărca statisticile.';
    const linesEl = document.getElementById('clientInterfaceAnalyticsSummaryLines');
    if (linesEl && tab === 'summary') {
      linesEl.innerHTML = '<span class="cia-summary-card__line">Nu s-au putut încărca statisticile.</span>';
    }
    if (isPopupOpen()) {
      const pane = document.querySelector(`#clientInterfaceAnalyticsPopup [data-cia-pane="${tab}"]`);
      if (pane) pane.insertAdjacentHTML('afterbegin', `<div class="dashboard-empty-state cia-error">${esc(msg)}</div>`);
      setTimeout(() => document.querySelectorAll('.cia-error').forEach((n) => n.remove()), 6000);
    }
  } finally {
    if (seq === loadSeq) {
      isLoading = false;
      setLoadingState(false);
    }
  }
}

export async function refreshClientInterfaceAnalytics(period) {
  if (period) activePeriod = period;
  renderPeriodButtons();
  // cardul mic + Rezumat folosesc mereu payload-ul perioadei
  await loadTab(isPopupOpen() && activeTab !== 'summary' ? activeTab : 'summary');
}

/** Re-randează sumarul (ex. când se actualizează revendicările). */
export function refreshClientInterfaceAnalyticsSummary() {
  renderAllViews();
}

async function resetClientInterfaceAnalytics() {
  if (isResetting || isLoading) return;
  const box = document.getElementById('ciaResetConfirm');
  if (box) box.hidden = true;

  const btn = document.getElementById('clientInterfaceAnalyticsResetBtn');
  isResetting = true;
  if (btn) {
    btn.disabled = true;
    btn.classList.add('client-analytics-reset-btn--busy');
  }

  try {
    await resetClientInterfaceAnalyticsBackend();
    activePeriod = 'total';
    monthsPayload = null;
    hoursPayload = null;
    activeTab = 'summary';
    await refreshClientInterfaceAnalytics('total');
    showSnackbar('Statisticile au fost resetate.', 'success');
  } catch (err) {
    logger.warn('[client-interface-analytics] reset failed:', err);
    showSnackbar('Nu am putut reseta statisticile.', 'error');
  } finally {
    isResetting = false;
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('client-analytics-reset-btn--busy');
    }
  }
}

function exportCsv() {
  const p = lastPayload;
  const series = Array.isArray(p?.series) ? p.series : [];
  if (!series.length) {
    showSnackbar('Alege o perioadă (nu „Total”) ca să descarci datele pe zile.');
    return;
  }
  const map = getMetricMap(p);
  const keys = MODULE_GRID_KEYS;
  const head = ['Data', 'Scanări QR', 'Interacțiuni', ...keys.map((k) => map[k]?.label || k)];
  const lines = [head.join(';')];
  series.forEach((d) => {
    lines.push([d.date, d.views, d.interactions, ...keys.map((k) => Number(d.counts?.[k]) || 0)].join(';'));
  });
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `statistici-${p.range?.from || 'start'}_${p.range?.to || 'azi'}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}

/** Raport PDF: pagină curată + „Tipărește → Salvează ca PDF” (merge pe telefon și PC, fără aplicații). */
async function exportPdf() {
  if (!hasFeature(getCurrentSettings() || {}, 'statsAdvanced')) {
    showSnackbar(`🔒 ${featureLockedMessage('statsAdvanced')}`, 'info');
    return;
  }
  const p = lastPayload;
  if (!p) return showSnackbar('Așteaptă să se încarce statisticile.');
  let heat = hoursPayload && hoursPayload._key === periodKey() ? hoursPayload.heat : null;
  if (!heat && p.range) {
    try {
      const h = await getClientInterfaceAnalyticsBackend(activePeriod, { ...periodParams(), withHeat: true });
      heat = h?.heat || null;
    } catch (_) { /* fără ore */ }
  }
  const s = getCurrentSettings() || {};
  const name = String(s.locationMapName || s.companyName || '').trim();
  const qr = getQrScans(p);
  const inter = Number(p.interactions) || 0;
  const prev = p.prev;
  const pct = (cur, old) => (old ? `${cur >= old ? '▲' : '▼'} ${Math.abs(Math.round(((cur - old) / old) * 100))}%` : cur ? 'nou' : '—');
  const top = getTopModule(p);
  const series = Array.isArray(p.series) ? p.series : [];
  const max = Math.max(1, ...series.map((d) => d.views));
  const bars = series.length > 1
    ? `<div class="rp-bars">${series.map((d) => `<span style="height:${Math.max(2, Math.round((d.views / max) * 100))}%"></span>`).join('')}</div>
       <div class="rp-axis"><span>${esc(dayLabel(series[0].date))}</span><span>${esc(dayLabel(series[series.length - 1].date))}</span></div>`
    : '';
  const map = getMetricMap(p);
  const pt = prev?.totals || {};
  const rows = MODULE_GRID_KEYS.map((k) => ({ label: map[k]?.label || k, cur: Number(map[k]?.value) || 0, old: Number(pt[k]) || 0 }))
    .sort((a, b) => b.cur - a.cur);
  let hoursTxt = '';
  if (heat && heat.total) {
    const g = heat.grid || [];
    const cells = g.map((row) => HEAT_BUCKETS.map((b) => row.slice(b.from, b.to).reduce((a, v) => a + v, 0)));
    const byDay = cells.map((r) => r.reduce((a, v) => a + v, 0));
    const byBucket = HEAT_BUCKETS.map((_, i) => cells.reduce((a, r) => a + r[i], 0));
    const b = HEAT_BUCKETS[byBucket.indexOf(Math.max(...byBucket))];
    hoursTxt = `<p class="rp-note">Cele mai multe scanări: <strong>${esc(RO_DAYS[byDay.indexOf(Math.max(...byDay))])}</strong>, între <strong>${b.from}:00 și ${b.to}:00</strong>.</p>`;
  }
  const today = new Date();
  const html = `
    <header class="rp-head">
      <div><div class="rp-brand">SpressJoy · Raport statistici</div>
      <h1>${esc(name || 'Locația ta')}</h1>
      <div class="rp-period">${esc(rangeLabel(p.range))}${prev ? ` · comparat cu ${esc(rangeLabel(prev))}` : ''}</div></div>
      <div class="rp-date">Generat ${today.getDate()} ${RO_MONTHS[today.getMonth()]} ${today.getFullYear()}</div>
    </header>
    <section class="rp-kpis">
      <div><span>Scanări QR</span><strong>${qr}</strong>${prev ? `<em>${pct(qr, prev.interfaceViews)}</em>` : ''}</div>
      <div><span>Interacțiuni</span><strong>${inter}</strong>${prev ? `<em>${pct(inter, prev.interactions)}</em>` : ''}</div>
      <div><span>Cel mai accesat</span><strong class="rp-txt">${esc(top ? top.label : '—')}</strong>${top ? `<em>${top.value} accesări</em>` : ''}</div>
    </section>
    ${bars ? `<h2>Scanări pe zile</h2>${bars}` : ''}
    ${hoursTxt}
    <h2>Interacțiuni pe module</h2>
    <table class="rp-table"><thead><tr><th>Modul</th><th>Acum</th>${prev ? '<th>Înainte</th><th>Diferență</th>' : ''}</tr></thead>
    <tbody>${rows.map((r) => `<tr><td>${esc(r.label)}</td><td><strong>${r.cur}</strong></td>${prev ? `<td>${r.old}</td><td>${pct(r.cur, r.old)}</td>` : ''}</tr>`).join('')}</tbody></table>
    <footer class="rp-foot">Orele sunt în ora României. Datele provin din scanările codului QR și apăsările clienților pe pagina locației.</footer>`;
  let box = document.getElementById('sjPrintReport');
  if (!box) {
    box = document.createElement('div');
    box.id = 'sjPrintReport';
    document.body.appendChild(box);
  }
  box.innerHTML = html;
  const oldTitle = document.title;
  document.title = `Statistici ${name || ''} ${p.range ? `${p.range.from} - ${p.range.to}` : ''}`.trim();
  document.body.classList.add('sj-printing-report');
  const done = () => {
    document.body.classList.remove('sj-printing-report');
    document.title = oldTitle;
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  showSnackbar('Alege „Salvează ca PDF” în fereastra care se deschide.');
  setTimeout(() => {
    try { window.print(); } catch (_) { done(); }
    setTimeout(() => { if (!window.matchMedia('print').matches) done(); }, 60000);
  }, 300);
}

function openRangePanel() {
  const panel = document.getElementById('ciaRangePanel');
  if (!panel) return;
  const today = localKey(new Date());
  const from = document.getElementById('ciaFrom');
  const to = document.getElementById('ciaTo');
  const r = lastPayload?.range;
  if (from) { from.max = today; from.value = customFrom || r?.from || today; }
  if (to) { to.max = today; to.value = customTo || r?.to || today; }
  panel.hidden = !panel.hidden;
}

function bindDashboardControls() {
  if (dashboardBound) return;
  dashboardBound = true;

  const periods = document.getElementById('clientInterfaceAnalyticsPeriods');
  if (periods) {
    periods.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-analytics-period]');
      if (!btn) return;
      const p = btn.getAttribute('data-analytics-period');
      if (p === 'custom' || p === 'month') {
        if (!hasFeature(getCurrentSettings() || {}, 'statsAdvanced')) {
          showSnackbar(`🔒 ${featureLockedMessage('statsAdvanced')}`, 'info');
          return;
        }
      }
      if (p === 'custom') {
        openRangePanel();
        return;
      }
      const panel = document.getElementById('ciaRangePanel');
      if (panel) panel.hidden = true;
      if (!p || p === activePeriod) return;
      refreshClientInterfaceAnalytics(p);
    });
  }

  document.getElementById('ciaRangePanel')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const from = document.getElementById('ciaFrom')?.value || '';
    const to = document.getElementById('ciaTo')?.value || '';
    if (!from || !to) return showSnackbar('Alege ambele date.');
    if (from > to) return showSnackbar('Data de început e după data de sfârșit.');
    customFrom = from;
    customTo = to;
    e.currentTarget.hidden = true;
    activePeriod = 'custom';
    refreshClientInterfaceAnalytics('custom');
  });

  document.getElementById('ciaTabs')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cia-tab]');
    if (!b || b.dataset.ciaTab === activeTab) return;
    if (b.dataset.ciaTab !== 'summary' && !hasFeature(getCurrentSettings() || {}, 'statsAdvanced')) {
      showSnackbar(`🔒 ${featureLockedMessage('statsAdvanced')}`, 'info');
      return;
    }
    activeTab = b.dataset.ciaTab;
    renderAllViews();
    if (activeTab === 'months' && !monthsPayload) loadTab('months');
    else if (activeTab === 'hours' && (!hoursPayload || hoursPayload._key !== periodKey())) loadTab('hours');
    else if (activeTab === 'summary' && (!lastPayload || lastPayload._key !== periodKey())) loadTab('summary');
  });

  document.getElementById('ciaMonthPrev')?.addEventListener('click', () => {
    activeMonth = shiftYm(activeMonth || currentYm(), -1);
    loadTab('months');
  });
  document.getElementById('ciaMonthNext')?.addEventListener('click', () => {
    const n = shiftYm(activeMonth || currentYm(), 1);
    if (n > currentYm()) return;
    activeMonth = n;
    loadTab('months');
  });
  document.getElementById('ciaMonthsChart')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cia-month]');
    if (!b || b.dataset.ciaMonth === activeMonth) return;
    activeMonth = b.dataset.ciaMonth;
    loadTab('months');
  });

  document.getElementById('ciaExportBtn')?.addEventListener('click', exportPdf);
  document.getElementById('ciaCsvBtn')?.addEventListener('click', exportCsv);

  const resetBtn = document.getElementById('clientInterfaceAnalyticsResetBtn');
  const confirmBox = document.getElementById('ciaResetConfirm');
  if (resetBtn) {
    resetBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!confirmBox) return;
      confirmBox.hidden = false;
      confirmBox.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }
  document.getElementById('ciaResetCancel')?.addEventListener('click', () => {
    if (confirmBox) confirmBox.hidden = true;
  });
  document.getElementById('ciaResetYes')?.addEventListener('click', () => {
    resetClientInterfaceAnalytics();
  });
}

export function initClientInterfaceAnalyticsDashboard() {
  bindDashboardControls();
  renderPeriodButtons();
}

export function openClientInterfaceAnalyticsPopup() {
  const popup = getPopup();
  if (!popup) return;
  bindDashboardControls();
  popup.classList.add('active');
  document.body.classList.add('cia-popup-open');
  renderAllViews();
  if (activeTab === 'summary' && !lastPayload && !isLoading) loadTab('summary');
  else if (activeTab === 'months' && !monthsPayload) loadTab('months');
  else if (activeTab === 'hours' && !hoursPayload) loadTab('hours');
}

export function closeClientInterfaceAnalyticsPopup() {
  const rc = document.getElementById('ciaResetConfirm');
  if (rc) rc.hidden = true;
  const popup = getPopup();
  if (popup) popup.classList.remove('active');
  document.body.classList.remove('cia-popup-open');
  const panel = document.getElementById('ciaRangePanel');
  if (panel) panel.hidden = true;
}

export function onClaimsDashboardVisible() {
  initClientInterfaceAnalyticsDashboard();
  refreshClientInterfaceAnalytics();
}
